import React, { useState, useEffect, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import { Clock } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useBusiness } from '../../../context/BusinessContext';
import api from '../../../utils/api';
import DashboardCard from '../DashboardCard';

function PendingBookingsCard({ dashboardContext }) {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [bookings, setBookings] = useState({ items: [], loading: true, error: null });

  const businessId = selectedBusiness?.business?._id;

  const fetchBookings = useCallback(async () => {
    if (!businessId) return;

    try {
      setBookings(prev => ({ ...prev, loading: true, error: null }));
      const weekStart = dashboardContext?.weekStartMs ? new Date(dashboardContext.weekStartMs) : new Date();
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      const isMyOverview = dashboardContext?.overviewScope === 'my';
      const userMatchIds = Array.isArray(dashboardContext?.userMatchIds)
        ? dashboardContext.userMatchIds.map((id) => String(id))
        : [];

      const response = await api.get('/api/unified-bookings', {
        params: {
          type: 'supplier',
          businessId,
          status: 'pending',
          startDate: weekStart.toISOString(),
          endDate: weekEnd.toISOString(),
        },
      });

      const allBookings = response.data.bookings || [];
      const pending = allBookings
        .filter((booking) => {
          if (!booking.startTime) return false;
          const startTime = new Date(booking.startTime);
          if (startTime < weekStart || startTime > weekEnd) return false;
          if (!isMyOverview || userMatchIds.length === 0) return true;
          const assignments = Array.isArray(booking.assignedTeamMembers) ? booking.assignedTeamMembers : [];
          return assignments.some((assignment) => {
            const assignmentIds = [
              assignment?.member,
              assignment?.staffMember,
              assignment?.member?.user?._id,
              assignment?.member?.user?.id,
              assignment?.staffMember?.user?._id,
              assignment?.staffMember?.user?.id,
              assignment?.member?._id,
              assignment?.member?.id,
              assignment?.staffMember?._id,
              assignment?.staffMember?.id,
            ].filter(Boolean);
            return assignmentIds.some((id) => userMatchIds.includes(String(id)));
          });
        })
        .sort((a, b) => new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id))
        .slice(0, 5)
        .map(booking => ({
          id: booking._id,
          createdAt: booking.createdAt,
          startTime: booking.startTime,
          clientName: booking.client
            ? `${booking.client.firstName || ''} ${booking.client.lastName || ''}`.trim() || 'Unknown'
            : 'Unknown',
          serviceType: booking.serviceType?.name || 'Unknown Service',
          status: booking.status,
        }));

      setBookings({ items: pending, loading: false, error: null });
    } catch (error) {
      setBookings({ items: [], loading: false, error: 'Failed to load bookings' });
    }
  }, [businessId, dashboardContext?.overviewScope, dashboardContext?.userMatchIds, dashboardContext?.weekStartMs]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  return (
    <DashboardCard
      title="Bookings Needing Approval"
      icon={<Clock size={18} weight="regular" />}
      iconBadgeCount={bookings.items.length}
      loading={bookings.loading}
      error={bookings.error}
      isEmpty={!bookings.loading && bookings.items.length === 0}
      emptyMessage="All caught up. Nothing is waiting for your approval. New requests from couples will appear here."
      emptyIcon={Clock}
      menuItems={[
        {
          label: 'View all',
          onClick: () => navigate('/supplier/bookings?view=overview'),
        },
      ]}
      iconBackgroundColor="rgba(245, 158, 11, 0.14)"
      iconColour="rgba(180, 83, 9, 1)"
      sx={{ height: '100%', minHeight: '400px' }}
    >
      {bookings.items.length > 0 && (
        <List dense>
        {bookings.items.map((booking, index) => (
          <React.Fragment key={booking.id}>
            <ListItem
              sx={{ px: 0, py: 1 }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/supplier/bookings?view=overview&booking=${booking.id}`);
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={500}>
                    {booking.clientName}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {booking.serviceType}
                    </Typography>
                    {booking.startTime && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        • {format(new Date(booking.startTime), 'MMM d')}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
            {index < bookings.items.length - 1 && <Divider />}
          </React.Fragment>
        ))}
        </List>
      )}
    </DashboardCard>
  );
}

export default PendingBookingsCard;
